import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  callMiniMaxAPI, setApiKey as setStoredApiKey, hasApiKey, getUsageStats,
  resetUsageStats, analyzeCornPurchase, analyzeFuturesMarket, analyzeRisk, analyzeData,
} from '../services/minimaxApi';
import { 
  callUnifiedAI, 
  getFeatureAIStatus, 
  isFeatureAIConfigured,
  hasAIConfig as hasUnifiedAIConfig
} from '../services/unifiedAIService';

// AI上下文
const AIContext = createContext(null);

// AI配置Provider
export const AIProvider = ({ children }) => {
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastError, setLastError] = useState(null);

  // 初始化检查API密钥（优先检查统一配置）
  useEffect(() => {
    const unifiedConfigured = hasUnifiedAIConfig();
    const minimaxConfigured = hasApiKey();
    setApiKeyConfigured(unifiedConfigured || minimaxConfigured);
    setUsageStats(getUsageStats());
    
    // 监听配置更新
    const handleConfigUpdate = () => {
      const unified = hasUnifiedAIConfig();
      const minimax = hasApiKey();
      setApiKeyConfigured(unified || minimax);
    };
    
    window.addEventListener('ai-config-updated', handleConfigUpdate);
    return () => window.removeEventListener('ai-config-updated', handleConfigUpdate);
  }, []);

  // 设置API密钥
  const setApiKey = useCallback((key) => {
    setStoredApiKey(key);
    setApiKeyConfigured(!!key);
  }, []);

  // 刷新使用统计
  const refreshUsageStats = useCallback(() => {
    setUsageStats(getUsageStats());
  }, []);

  // 重置统计
  const handleResetStats = useCallback(() => {
    resetUsageStats();
    setUsageStats(getUsageStats());
  }, []);

  // 通用AI调用（优先使用统一服务）
  const callAI = useCallback(async (prompt, options = {}) => {
    setLoading(true);
    setLastError(null);
    
    try {
      let result;
      
      // 优先使用统一AI服务（如果已配置）
      if (hasUnifiedAIConfig() && options.featureId) {
        result = await callUnifiedAI(prompt, options);
      } else if (hasUnifiedAIConfig()) {
        // 没有指定功能ID，使用默认配置
        result = await callUnifiedAI(prompt, options);
      } else if (apiKeyConfigured) {
        // 回退到MiniMax
        result = await callMiniMaxAPI(prompt, options);
      } else {
        return { 
          success: false, 
          error: '请先在"系统管理 > AI配置"中添加并配置AI服务' 
        };
      }
      
      refreshUsageStats();
      if (!result.success) setLastError(result.error);
      return result;
    } catch (error) {
      setLastError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [apiKeyConfigured, refreshUsageStats]);


  // 期货行情AI分析
  const analyzeMarket = useCallback(async (data) => {
    if (!apiKeyConfigured) {
      return { success: false, error: '请先配置MiniMax API密钥' };
    }
    setLoading(true);
    try {
      const result = await analyzeFuturesMarket(data);
      refreshUsageStats();
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [apiKeyConfigured, refreshUsageStats]);

  // 风险评估AI分析
  const analyzeRiskData = useCallback(async (data) => {
    if (!apiKeyConfigured) {
      return { success: false, error: '请先配置MiniMax API密钥' };
    }
    setLoading(true);
    try {
      const result = await analyzeRisk(data);
      refreshUsageStats();
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [apiKeyConfigured, refreshUsageStats]);

  // 玉米采购AI分析
  const analyzeCorn = useCallback(async (data) => {
    if (!apiKeyConfigured) {
      return { success: false, error: '请先配置MiniMax API密钥' };
    }
    setLoading(true);
    try {
      const result = await analyzeCornPurchase(data);
      refreshUsageStats();
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [apiKeyConfigured, refreshUsageStats]);

  // 通用数据分析
  const analyzeGeneral = useCallback(async (data, context = '') => {
    if (!apiKeyConfigured) {
      return { success: false, error: '请先配置MiniMax API密钥' };
    }
    setLoading(true);
    try {
      const result = await analyzeData(data, context);
      refreshUsageStats();
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [apiKeyConfigured, refreshUsageStats]);

  // 内控分析
  const analyzeInternalControl = useCallback(async (data) => {
    if (!apiKeyConfigured) {
      return { success: false, error: '请先配置MiniMax API密钥' };
    }
    setLoading(true);
    try {
      const prompt = `请对以下白羽肉鸡产业链内控数据进行专业分析：

数据概览：
${JSON.stringify(data, null, 2)}

请从以下方面进行分析：
1. 成本结构分析
2. 关键风险点识别
3. 内控措施建议
4. 优化方向建议`;

      const result = await callMiniMaxAPI(prompt, {
        systemPrompt: '你是一个专业的畜牧业内控分析师，精通白羽肉鸡产业链的成本控制和风险管理。',
      });
      refreshUsageStats();
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [apiKeyConfigured, refreshUsageStats]);

  const value = {
    apiKeyConfigured,
    setApiKey,
    usageStats,
    refreshUsageStats,
    resetStats: handleResetStats,
    loading,
    lastError,
    callAI,
    analyzeMarket,
    analyzeRiskData,
    analyzeCorn,
    analyzeGeneral,
    analyzeInternalControl,
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
};

// 使用AI上下文的Hook
export const useAI = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
};

export default AIContext;
